import { inject, Injectable, OnDestroy, signal } from "@angular/core";
import { ProjValuesService } from "./proj-values.service";
import { HistoryRecord, PlanRecord, PlanUpdateRecord, PreviewPlan, SettingsResponse } from "@shared/models";
import { PreviewTableService } from "./preview-table.service";
import { GlobalService, HistoryApiService, PlanApiService, SettingsApiService } from "@shared/services";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { finalize, Subscription, tap } from "rxjs";
import { convertToMinutes } from "@shared/functions";
import { getContractualMonth } from "@shared/functions/date.functions";
import { CacheId } from "@shared/enums";
import { Router } from "@angular/router";
import { MessageService } from "primeng/api";

@Injectable({
  providedIn: "root",
})
export class PresetPlansService implements OnDestroy {
  projValuesService = inject(ProjValuesService);
  previewTableService = inject(PreviewTableService);
  historyApiService = inject(HistoryApiService);
  plansApiService = inject(PlanApiService);
  // settingsApiService = inject(SettingsApiService);
  router = inject(Router);
  globalService = inject(GlobalService);
  messageService = inject(MessageService);

  history: HistoryRecord[] = [];
  standbyPlan: PlanRecord[] = [];
  // settings: SettingsResponse = {} as SettingsResponse;
  isLoading = signal<boolean>(false);

  private updateSubscription: Subscription | null = null;

  constructor() {
    this.isLoading.set(true);
    this.historyApiService
      .getHistory(false)
      .pipe(
        takeUntilDestroyed(),
        tap((response) => {
          if (response.response) {
            this.history = response.response.history;
          }
        })
      )
      .subscribe();

    // this.settingsApiService
    //   .getSettings()
    //   .pipe(
    //     takeUntilDestroyed(),
    //     tap((response) => {
    //       if (response.response) {
    //         this.settings = response.response;
    //       }
    //     })
    //   )
    //   .subscribe();

    this.plansApiService
      .getPlan(false, 1)
      .pipe(
        takeUntilDestroyed(),
        tap((response) => {
          if (response.response) {
            this.standbyPlan = response.response.plans;
          }
        }),
        finalize(() => {
          this.isLoading.set(false);
        })
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    if (this.updateSubscription) {
      this.updateSubscription.unsubscribe();
    }
  }

  getPreviewTable(planType: string, vmaxDefault: string, projDefault: string): PreviewPlan[] {
    return this.previewTableService.getPreviewTable(planType, convertToMinutes(projDefault));
  }

  createPlan(planType: string, vmaxDefault: string, projDefault: string): void {
    const newPlanValues: PlanUpdateRecord[] = [];
    const { startMonth, startMonthMinus1, startMonthMinus2, projValues } = this.projValuesService.getProjValues(
      planType,
      convertToMinutes(projDefault)
    );

    let useTemplate = false;
    let templateIndex = 0;

    for (let i = 0; i < 36; i++) {
      const monthAsInt = this.standbyPlan[i].effectivePeriod % 100;
      const month = getContractualMonth({ month: monthAsInt }).monthShort;
      let projValue = convertToMinutes(projDefault);

      if (month === startMonthMinus1 || month === startMonthMinus2) {
        projValue = 3600;
      } else if (month === startMonth) {
        useTemplate = true;
      }
      if (useTemplate) {
        projValue = projValues[templateIndex % 12];
        templateIndex++;
      }

      const planRecord: PlanUpdateRecord = {
        key: this.standbyPlan[i].key,
        effectivePeriod: this.standbyPlan[i].effectivePeriod,
        vmax: convertToMinutes(vmaxDefault),
        proj: projValue,
        imax: 0,
      };
      newPlanValues.push(planRecord);
    }
    const combinedData = [...this.getHistoryProj(), ...newPlanValues];
    const calculatedData = this.calculateImax(combinedData).slice(9);
    this.globalService.saving.set(true);
    this.updateSubscription = this.plansApiService
      .updatePlanRecords(calculatedData)
      .pipe(
        tap((response) => {
          if (response) {
            const standbyPlan = localStorage.getItem(CacheId.StandbyPlan);
            if (standbyPlan) {
              const parsedStandbyPlan = JSON.parse(standbyPlan);
              parsedStandbyPlan.plans = response;
              localStorage.setItem(CacheId.StandbyPlan, JSON.stringify(parsedStandbyPlan));
              localStorage.removeItem(CacheId.StandbyPlanEdits);
              this.router.navigate(["/standby"]);
              this.messageService.clear();
              this.messageService.add({
                severity: "success",
                summary: "Success",
                detail: "Plan successfully created",
                key: "noReply",
              });
            }
          }
        }),
        finalize(() => {
          this.globalService.saving.set(false);
        })
      )
      .subscribe();
  }

  private getHistoryProj(): { proj: number }[] {
    const historyProj = this.history.map((record) => {
      return { proj: record.projMinutes };
    });
    return historyProj.slice(-10).slice(0, 9);
  }

  private calculateImax(data: any[]) {
    const totalRows = data.length;
    //  Takes the 9th row and begins calculation on future rows
    for (let i = 8; i < totalRows - 1; i++) {
      let sumOfPrevProj = 0;
      /**  Adds the proj from the previous 9 rows, excluding the first row previous.
       *   Converts the time to minutes. */
      for (let j = i - 8; j < i; j++) {
        sumOfPrevProj += data[j].proj;
      }
      /**  Takes the summed projs and runs it through the imax formula,
       *   appending the calculated value to the next row down from i. */
      const imaxValue = 1080 * 60 - (sumOfPrevProj + 246 * 60);
      data[i + 1].imax = imaxValue;
    }
    return data;
  }
}
